import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useLeague } from '../../contexts/LeagueContext'
import { useUser } from '../../contexts/UserContext'
import { announcements } from '../../lib/api'
import { parseLocalDate, formatTime } from '../../utils/dateHelpers'

/**
 * SubRequestBanner - Persistent alert for open sub requests
 *
 * Shows a small banner at the top of all league pages when there are
 * open sub requests. Provides quick access to respond.
 */
export default function SubRequestBanner() {
  const location = useLocation()
  const { currentLeague } = useLeague()
  const { user, isAuthenticated } = useUser()
  const [subRequests, setSubRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [selectedSub, setSelectedSub] = useState(null)
  const [respondMessage, setRespondMessage] = useState('')
  const [respondLoading, setRespondLoading] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  // Fetch sub requests when league changes
  useEffect(() => {
    const fetchSubRequests = async () => {
      if (!currentLeague?.id || !isAuthenticated) {
        setSubRequests([])
        setLoading(false)
        return
      }

      try {
        const data = await announcements.getSubRequests(currentLeague.id)
        const openRequests = (data || []).filter(sr => sr.sub_status === 'open')
        setSubRequests(openRequests)
        setDismissed(false) // Reset dismissed state when data changes
      } catch (error) {
        console.error('Error fetching sub requests:', error)
        setSubRequests([])
      } finally {
        setLoading(false)
      }
    }

    fetchSubRequests()
    // Refresh every 60 seconds
    const interval = setInterval(fetchSubRequests, 60000)
    return () => clearInterval(interval)
  }, [currentLeague?.id, isAuthenticated])

  // Handle responding to a sub request
  const handleRespond = async () => {
    if (!selectedSub || !user) return
    setRespondLoading(true)
    try {
      await announcements.respondToSubRequest(selectedSub.id, respondMessage)
      // Remove from list
      setSubRequests(prev => prev.filter(sr => sr.id !== selectedSub.id))
      setShowModal(false)
      setSelectedSub(null)
      setRespondMessage('')
    } catch (error) {
      console.error('Error responding to sub request:', error)
      alert(error.message || 'Failed to respond')
    } finally {
      setRespondLoading(false)
    }
  }

  // Don't show if no sub requests, loading, or dismissed
  if (loading || subRequests.length === 0 || dismissed || !isAuthenticated) {
    return null
  }

  // Get the league base path for the overview link
  const leagueBasePath = currentLeague?.slug
    ? `/${currentLeague.slug}`
    : `/leagues/${currentLeague?.id}`

  // Check if we're already on the overview page
  const isOnOverview = location.pathname === leagueBasePath || location.pathname === leagueBasePath + '/'

  const firstSub = subRequests[0]
  const count = subRequests.length

  return (
    <>
      {/* Banner */}
      <div className="bg-orange-500 text-white px-4 py-2 -mx-4 lg:-mx-6 -mt-4 lg:-mt-6 mb-4 lg:mb-6">
        <div className="flex items-center justify-between gap-3 max-w-7xl mx-auto">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            <span className="text-sm font-medium truncate">
              {count === 1 ? (
                <>Sub needed for {firstSub.home_team_name} vs {firstSub.away_team_name}</>
              ) : (
                <>{count} sub requests need players</>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {count === 1 ? (
              <button
                onClick={() => { setSelectedSub(firstSub); setShowModal(true) }}
                className="px-3 py-1 text-sm font-medium bg-white text-orange-600 rounded hover:bg-orange-50 transition-colors"
              >
                I Can Sub
              </button>
            ) : isOnOverview ? (
              <span className="text-sm text-orange-100">See below</span>
            ) : (
              <Link
                to={leagueBasePath}
                className="px-3 py-1 text-sm font-medium bg-white text-orange-600 rounded hover:bg-orange-50 transition-colors"
              >
                View All
              </Link>
            )}
            <button
              onClick={() => setDismissed(true)}
              className="p-1 hover:bg-orange-600 rounded transition-colors"
              title="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Response Modal */}
      {showModal && selectedSub && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Respond to Sub Request</h3>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
              <p className="text-sm font-medium text-orange-900">
                {selectedSub.home_team_name} vs {selectedSub.away_team_name}
              </p>
              <p className="text-sm text-orange-700 mt-1">
                {selectedSub.game_date && parseLocalDate(selectedSub.game_date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                {selectedSub.game_time && ` at ${formatTime(selectedSub.game_time)}`}
              </p>
              {selectedSub.rink_name && (
                <p className="text-xs text-orange-600 mt-1">{selectedSub.rink_name}</p>
              )}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Message (optional)
              </label>
              <textarea
                value={respondMessage}
                onChange={(e) => setRespondMessage(e.target.value)}
                placeholder="Add a message for the captain..."
                className="w-full p-2 border border-slate-300 rounded-lg text-sm"
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowModal(false); setSelectedSub(null); setRespondMessage('') }}
                className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                disabled={respondLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleRespond}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
                disabled={respondLoading}
              >
                {respondLoading ? 'Sending...' : 'I Can Sub'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
